import { Supabase } from "../Utils/supabase.config";
export const getUserData = async (userId) => {
  try {
    const { data, error } = await Supabase
      .from('users')
      .select()
      .eq('id', userId)
      .single();
    if (error) return { success: false, msg: error?.message };
    return { success: true, data };
  } catch (error) {
    return { success: false, msg: error.message };
  }
};

// Role priority hierarchy (higher number = higher privilege)
const ROLE_PRIORITY = {
  'guest': 50,
  'admin': 40,
  'special': 30,
  'user': 20,  
  'super_admin': 10, // Handle both cases
};

// Get user's app-wide role from user_roles table
// Returns the HIGHEST privilege role if user has multiple roles
export const getUserRole = async (userId) => {
  try {
    const { data, error } = await Supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);
    
    if (error || !data || data.length === 0) {
      // If no role found, return default role
      return { success: true, data: { role: 'guest' } };
    }
    
    // If user has multiple roles, select the one with highest priority
    let highestRole = 'guest';
    let highestPriority = 0;
    
    data.forEach(roleEntry => {
      const role = roleEntry.role;
      const priority = ROLE_PRIORITY[role] || 0;
      
      if (priority > highestPriority) {
        highestPriority = priority;
        highestRole = role;
      }
    });
    
    return { success: true, data: { role: highestRole } };
  } catch (error) {
    // If any error, return default role
    return { success: true, data: { role: 'guest' } };
  }
};

// Check if user can access admin features (reports, etc.)
export const canAccessAdminFeatures = async (userId) => {
  try {
    const { data, error } = await Supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .in('role', ['admin', 'super_admin'])
      .single();
    
    if (error) {
      return { success: true, data: false };
    }
    
    return { success: true, data: !!data };
  } catch (error) {
    return { success: true, data: false };
  }
};

export const updateUser = async (userId, data)=>{
    try{
        const { error } = await Supabase
        .from('users')
        .update(data)
        .eq('id', userId);

        if(error){
            return {success: false, msg: error?.message};
        }
        
        return {success: true};
    }catch(error){
        return {success: false, msg: error.message};
    }
}

export const assignSpecialRole = async (userId, grantedBy = null) => {
  try {
    const { error } = await Supabase
      .from('user_roles')
      .insert({ user_id: userId, role: 'special', granted_by: grantedBy })
      .select()
      .maybeSingle();

    if (error && error.code !== '23505') { // ignore duplicate role
      return { success: false, msg: error.message };
    }

    return { success: true };
  } catch (error) {
    return { success: false, msg: error.message };
  }
};

export const setDivasPoints = async (userId, points) => {
  try {
    const { error } = await Supabase
      .from('users')
      .update({ divas_points: points })
      .eq('id', userId);

    if (error) {
      return { success: false, msg: error.message };
    }

    return { success: true };
  } catch (error) {
    return { success: false, msg: error.message };
  }
};

export const incrementDivasPoints = async (userId, delta = 1) => {
  try {
    const { data, error } = await Supabase
      .from('users')
      .select('divas_points')
      .eq('id', userId)
      .single();

    if (error) {
      return { success: false, msg: error.message };
    }

    const current = data?.divas_points || 0;
    return await setDivasPoints(userId, current + delta);
  } catch (error) {
    return { success: false, msg: error.message };
  }
};

// Log Divas Points change to existing divas_points_logs table
// Only creates log entries when there are actual changes to bonus_points or divas_points
// Note: amount column is not updated - left as default/unchanged
export const logDivasPointsChange = async (userIntegerId, divasPoints, entrySource, createdBy = 0, bonusPoints = 0) => {
  // Ensure we only log when there are actual changes (at least one non-zero value)
  if (divasPoints === 0 && bonusPoints === 0) {
    console.warn('Skipping log entry: both divas_points and bonus_points are zero');
    return { success: true, data: null }; // Return success but don't create entry
  }

  try {
    const { data, error } = await Supabase
      .from('divas_points_logs')
      .insert([{
        user_id: userIntegerId, // bigint user_id from users table
        divas_points: divasPoints, // the change amount (can be positive or negative)
        bonus_points: bonusPoints || 0, // the change amount for bonus points (can be positive or negative)
        // amount field is not updated - left as default/unchanged
        entry_source: entrySource, // 1 = manual, 2 = spin wheel
        created_by: createdBy || 0,
      }])
      .select()
      .single();

    if (error) {
      console.error('Error logging divas points:', error);
      return { success: false, msg: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error logging divas points:', error);
    return { success: false, msg: error.message };
  }
};

